import { Project, LegacyKeywordIntent, LEGACY_INTENT_COLORS, LEGACY_INTENT_LABELS, IntentCategoryConfig } from '../types';

// Re-export for convenience
export type { IntentCategoryConfig };

/**
 * Auto-formats an intent category name into a readable label
 * Examples:
 * - "category-anchor" -> "Category Anchor"
 * - "preparation-format" -> "Preparation Format"
 * - "attributes_ingredients" -> "Attributes Ingredients"
 */
export function formatIntentLabel(category: string): string {
  return category
    .split(/[-_]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Generates a color class for a new intent category
 * Uses a large diverse color palette with consistent perceptual brightness
 * All colors use ONLY standard Tailwind colors with uniform 100/800 pattern
 */
const NEW_CATEGORY_COLORS = [
  // Primary set - 100 background / 800 text (NO yellow/amber - too bright)
  'bg-cyan-100 text-cyan-800 border-cyan-200',
  'bg-teal-100 text-teal-800 border-teal-200',
  'bg-sky-100 text-sky-800 border-sky-200',
  'bg-stone-100 text-stone-800 border-stone-200',
  'bg-zinc-100 text-zinc-800 border-zinc-200',
  'bg-slate-100 text-slate-800 border-slate-200',
  'bg-neutral-100 text-neutral-800 border-neutral-200',
  'bg-rose-100 text-rose-800 border-rose-200',
  'bg-violet-100 text-violet-800 border-violet-200',
  'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',
  'bg-emerald-100 text-emerald-800 border-emerald-200',
  'bg-indigo-100 text-indigo-800 border-indigo-200',
  'bg-pink-100 text-pink-800 border-pink-200',
  'bg-purple-100 text-purple-800 border-purple-200',
  'bg-blue-100 text-blue-800 border-blue-200',
  'bg-green-100 text-green-800 border-green-200',
  'bg-red-100 text-red-800 border-red-200',
  'bg-gray-100 text-gray-800 border-gray-200',
  'bg-orange-100 text-orange-900 border-orange-200',
  
  // Secondary set - 200 background / 900 text (slightly darker)
  'bg-cyan-200 text-cyan-900 border-cyan-300',
  'bg-teal-200 text-teal-900 border-teal-300',
  'bg-sky-200 text-sky-900 border-sky-300',
  'bg-emerald-200 text-emerald-900 border-emerald-300',
  'bg-indigo-200 text-indigo-900 border-indigo-300',
  'bg-pink-200 text-pink-900 border-pink-300',
  'bg-purple-200 text-purple-900 border-purple-300',
  'bg-blue-200 text-blue-900 border-blue-300',
  'bg-green-200 text-green-900 border-green-300',
  'bg-red-200 text-red-900 border-red-300',
  'bg-rose-200 text-rose-900 border-rose-300',
  'bg-violet-200 text-violet-900 border-violet-300',
  'bg-fuchsia-200 text-fuchsia-900 border-fuchsia-300',
  'bg-slate-200 text-slate-900 border-slate-300',
  'bg-stone-200 text-stone-900 border-stone-300',
  'bg-neutral-200 text-neutral-900 border-neutral-300',
  'bg-zinc-200 text-zinc-900 border-zinc-300',
  'bg-gray-200 text-gray-900 border-gray-300',
  'bg-orange-200 text-orange-900 border-orange-300',
  
  // Tertiary set - 50 background / 700 text (very light)
  'bg-cyan-50 text-cyan-700 border-cyan-100',
  'bg-teal-50 text-teal-700 border-teal-100',
  'bg-sky-50 text-sky-700 border-sky-100',
  'bg-emerald-50 text-emerald-700 border-emerald-100',
  'bg-indigo-50 text-indigo-700 border-indigo-100',
  'bg-violet-50 text-violet-700 border-violet-100',
  'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100',
  'bg-rose-50 text-rose-700 border-rose-100',
  'bg-pink-50 text-pink-700 border-pink-100',
  'bg-purple-50 text-purple-700 border-purple-100',
  'bg-blue-50 text-blue-700 border-blue-100',
  'bg-green-50 text-green-700 border-green-100',
  'bg-red-50 text-red-700 border-red-100',
  'bg-slate-50 text-slate-700 border-slate-100',
  'bg-stone-50 text-stone-700 border-stone-100',
  'bg-zinc-50 text-zinc-700 border-zinc-100',
  'bg-neutral-50 text-neutral-700 border-neutral-100',
  'bg-gray-50 text-gray-700 border-gray-100',
  'bg-orange-50 text-orange-800 border-orange-100',
];

/**
 * Generates a consistent color for a category based on its name
 * Uses legacy colors if the category matches, otherwise assigns from palette
 */
export function getIntentColor(category: string): string {
  // Check if it's a legacy category
  if (category in LEGACY_INTENT_COLORS) {
    return LEGACY_INTENT_COLORS[category as LegacyKeywordIntent];
  }

  // Generate consistent color based on category name hash
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = ((hash << 5) - hash) + category.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  const colorIndex = Math.abs(hash) % NEW_CATEGORY_COLORS.length;
  return NEW_CATEGORY_COLORS[colorIndex];
}

/**
 * Gets the intent label for a category
 * Uses legacy labels if available, otherwise auto-formats
 */
export function getIntentLabel(category: string): string {
  if (category in LEGACY_INTENT_LABELS) {
    return LEGACY_INTENT_LABELS[category as LegacyKeywordIntent];
  }
  return formatIntentLabel(category);
}

/**
 * Gets the intent category config for a project
 * Returns project-specific config if available, otherwise falls back to legacy
 */
export function getIntentCategoryConfig(project: Project | null): Record<string, IntentCategoryConfig> {
  if (project?.intentCategoryConfig) {
    return project.intentCategoryConfig;
  }

  // Fallback to legacy categories
  const legacyConfig: Record<string, IntentCategoryConfig> = {};
  Object.keys(LEGACY_INTENT_LABELS).forEach(key => {
    legacyConfig[key] = {
      label: LEGACY_INTENT_LABELS[key as LegacyKeywordIntent],
      color: LEGACY_INTENT_COLORS[key as LegacyKeywordIntent],
    };
  });
  return legacyConfig;
}

/**
 * Gets the list of intent categories for a project
 * Returns project-specific categories if available, otherwise falls back to legacy
 */
export function getIntentCategories(project: Project | null): string[] {
  if (project?.intentCategories && project.intentCategories.length > 0) {
    return project.intentCategories;
  }

  // Fallback to legacy categories
  return Object.keys(LEGACY_INTENT_LABELS) as string[];
}

/**
 * Gets the color for an intent category in a project context
 * Uses project-specific config if available, otherwise assigns unique colors based on category position
 */
export function getIntentColorForProject(project: Project | null, category: string): string {
  if (project?.intentCategoryConfig?.[category]) {
    return project.intentCategoryConfig[category].color;
  }

  // Check if it's a legacy category first
  if (category in LEGACY_INTENT_COLORS) {
    return LEGACY_INTENT_COLORS[category as LegacyKeywordIntent];
  }

  // For non-legacy categories, assign color based on position in project's intent list
  if (project?.intentCategories) {
    const allCategories = project.intentCategories;
    
    // Count how many non-legacy categories come before this one
    let nonLegacyIndex = 0;
    for (let i = 0; i < allCategories.length; i++) {
      if (allCategories[i] === category) {
        // Found our category - use the nonLegacyIndex for color assignment
        return NEW_CATEGORY_COLORS[nonLegacyIndex % NEW_CATEGORY_COLORS.length];
      }
      // Only increment counter if this is not a legacy category
      if (!(allCategories[i] in LEGACY_INTENT_COLORS)) {
        nonLegacyIndex++;
      }
    }
  }

  // Fallback to hash-based assignment if category not found in project list
  return getIntentColor(category);
}

/**
 * Gets the label for an intent category in a project context
 * Uses project-specific config if available, otherwise falls back to legacy or auto-formats
 */
export function getIntentLabelForProject(project: Project | null, category: string): string {
  if (project?.intentCategoryConfig?.[category]) {
    return project.intentCategoryConfig[category].label;
  }

  // Fallback to legacy or auto-format
  return getIntentLabel(category);
}

